import React, { useState, useEffect, useCallback } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { carouselData } from '../../data/carouselData';
import './HeroCarousel.css';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = carouselData.length;
  const autoPlayInterval = 12000;
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  // Auto play functionality with reset capability
  const [autoPlayKey, setAutoPlayKey] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [totalSlides, autoPlayInterval, autoPlayKey]);

  // Function to reset the auto-play timer
  const resetAutoPlayTimer = useCallback(() => {
    setAutoPlayKey(prev => prev + 1);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    resetAutoPlayTimer();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    resetAutoPlayTimer();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    resetAutoPlayTimer();
  };

  // Handle image load error
  const handleImageError = (slideIndex) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [slideIndex]: true
    }));
  };

  // Handle different button clicks based on current slide
  const handleButtonClick = () => {
    switch (currentSlide) {
      case 0: // First slide - My Expertise
        const expertiseSection = document.querySelector('.what-i-bring-container');
        if (expertiseSection) {
          expertiseSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      case 1: // Second slide - About Me
        const aboutSection = document.querySelector('.about-section');
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      case 2: // Third slide - Contact With Me
        const footer = document.querySelector('.app-footer');
        if (footer) {
          footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        break;
      default:
        break;
    }
  };

  // Get button text based on current slide
  const getButtonText = () => {
    switch (currentSlide) {
      case 0:
        return 'My Expertise';
      case 1:
        return 'About Me';
      case 2:
        return 'Contact With Me';
      default:
        return 'Learn More';
    }
  };

  return (
    <div className="carousel-container">
      <div className="hero-carousel">
        {/* Carousel slides */}
        <div
          className={`carousel-slide slide-${currentSlide + 1}`}
          style={{
            backgroundImage: `url(${carouselData[currentSlide].image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Hidden image for error detection */}
          <img
            src={carouselData[currentSlide].image}
            alt=""
            style={{ display: 'none' }}
            onError={() => handleImageError(currentSlide)}
          />

          {/* Diagonal mask overlay - PC only */}
          <div key={`mask-${currentSlide}`} className="diagonal-mask"></div>

          {/* Slide content */}
          <div className="slide-content">
            <div key={`content-${currentSlide}`} className="content-wrapper">
              <h1 className="slide-title">{carouselData[currentSlide].title}</h1>
              <p className="slide-subtitle">{carouselData[currentSlide].subtitle}</p>
              <p className="slide-description">{carouselData[currentSlide].description}</p>
              <button className="contact-btn" onClick={handleButtonClick}>{getButtonText()}</button>
            </div>
          </div>

          {/* Fallback gradient overlay when image is not available */}
          <div
            className="slide-overlay"
            style={{
              opacity: imageLoadErrors[currentSlide] ? 1 : 0
            }}
          ></div>
        </div>

        {/* Navigation buttons */}
        <button className="carousel-nav-btn prev-btn" onClick={prevSlide}>
          <LeftOutlined />
        </button>
        <button className="carousel-nav-btn next-btn" onClick={nextSlide}>
          <RightOutlined />
        </button>

        {/* Slide indicators */}
        <div className="slide-indicators">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`indicator-dot ${currentSlide === index ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
